/**
 * @file Shop Rates Form.
 */

import { logPtEvent } from '../../js/utils/pt-events';

const ZIP_STORAGE_KEY = 'zipCode';
const ZIP_PATTERN = /^\d{5}$/;

/**
 * Identify the page surface containing a Shop Rates form.
 *
 * @param {Element} shopRatesForm - Shop Rates component wrapper.
 * @returns {string} Stable placement identifier.
 */
const getFormPlacement = (shopRatesForm) => {
	if (shopRatesForm.closest('.v-rate-cta')) return 'rate-cta';
	if (shopRatesForm.closest('.v-hero__form')) return 'hero';
	if (shopRatesForm.closest('.v-mobile-menu')) return 'mobile-menu';
	if (shopRatesForm.closest('.v-header__nav-submenu')) return 'desktop-header';

	return 'standalone';
};

/**
 * Get a valid ZIP Code from local storage.
 * @returns {string} Stored ZIP Code, or an empty string.
 */
const getStoredZipCode = () => {
	try {
		const zipCode = window.localStorage.getItem(ZIP_STORAGE_KEY) || '';
		return ZIP_PATTERN.test(zipCode) ? zipCode : '';
	} catch {
		return '';
	}
};

/**
 * Save a ZIP Code to local storage when storage is available.
 * @param {string} zipCode - Valid five-digit ZIP Code.
 */
const storeZipCode = (zipCode) => {
	try {
		window.localStorage.setItem(ZIP_STORAGE_KEY, zipCode);
	} catch {
		// Continue submitting when local storage is unavailable.
	}
};

/**
 * Shop Rates Form component.
 */
const vShopRatesForm = () => {
	const shopRatesForms = document.querySelectorAll('.v-shop-rates-form');

	if (!shopRatesForms.length) {
		return;
	}

	const storedZipCode = getStoredZipCode();

	shopRatesForms.forEach((shopRatesForm, index) => {
		const form = shopRatesForm.querySelector('.v-shop-rates-form__form');
		const zipInput = form.querySelector('.v-shop-rates-form__input');
		const placement = getFormPlacement(shopRatesForm);

		if (storedZipCode) {
			zipInput.value = storedZipCode;
		}

		form.addEventListener('submit', (event) => {
			event.preventDefault();
			const formData = new FormData(form);
			const type = formData.get('type') || '';
			const zip = formData.get('zip') || '';
			const action = form.dataset[`${type}Action`];
			const shouldAppendSolarParam = form.dataset.solarCta === 'true';

			if (action && zip && ZIP_PATTERN.test(zip)) {
				storeZipCode(zip);

				const lastChar = action.slice(-1);
				let target = action + (lastChar === '/' ? '' : '/') + zip;

				if (shouldAppendSolarParam) {
					target += target.includes('?')
						? '&solarSkip=true'
						: '?solarSkip=true';
				}

				logPtEvent(
					'site',
					'wordpress_shop_rates_form_submitted',
					['frontend'],
					{
						component: 'shop-rates-form',
						formPosition: index + 1,
						placement,
						pagePath: window.location.pathname,
						pageTitle: document.title,
						customerType: type,
						zipCode: zip,
						solarCta: shouldAppendSolarParam,
						destination: target,
					},
				);

				window.location = target;
			} else if (!zip || !ZIP_PATTERN.test(zip)) {
				alert('Please enter a valid 5 digit ZIP Code.');
			}
		});
	});
};

export default vShopRatesForm;
